// Copyright 2026, University of Maryland, All Rights Reserved
import type { FC } from 'react'
import { Switch } from '@/components/ui'

interface Props {
  extractTrackedChanges: boolean
  onExtractTrackedChangesChange: (value: boolean) => void
  acceptTrackedChanges: boolean
  onAcceptTrackedChangesChange: (value: boolean) => void
  extractComments: boolean
  onExtractCommentsChange: (value: boolean) => void
}

export const DocxOptionsSection: FC<Props> = ({
  extractTrackedChanges,
  onExtractTrackedChangesChange,
  acceptTrackedChanges,
  onAcceptTrackedChangesChange,
  extractComments,
  onExtractCommentsChange,
}) => (
  <div>
    <h3 className="text-lg font-semibold mb-3">DOCX Options</h3>
    <p className="text-sm text-muted-foreground mb-4">
      Behavior for tracked changes and comments in .docx uploads. All three are
      off by default — opt in per pipeline as needed.
    </p>

    <div className="space-y-3">
      <label className="flex cursor-pointer items-center justify-between gap-4 rounded-md border border-border p-3 hover:bg-muted/50 transition-colors">
        <div>
          <span className="block text-sm font-medium">Extract tracked changes</span>
          <span className="block text-xs text-muted-foreground">
            Emit document text with inline [INS]/[DEL] markers so the LLM sees
            insertions and deletions. Required for any pipeline that analyzes
            tracked changes.
          </span>
        </div>
        <Switch
          checked={extractTrackedChanges}
          onCheckedChange={onExtractTrackedChangesChange}
          className="flex-shrink-0"
        />
      </label>

      <label className="flex cursor-pointer items-center justify-between gap-4 rounded-md border border-border p-3 hover:bg-muted/50 transition-colors">
        <div>
          <span className="block text-sm font-medium">Accept tracked changes</span>
          <span className="block text-xs text-muted-foreground">
            Strip tracked-change markup before PDF conversion so the
            extracted text stream is clean. When off, Gotenberg renders
            insertions and deletions inline and extraction conflates the two
            (e.g. "187" becomes "1878").
          </span>
        </div>
        <Switch
          checked={acceptTrackedChanges}
          onCheckedChange={onAcceptTrackedChangesChange}
          className="flex-shrink-0"
        />
      </label>

      <label className="flex cursor-pointer items-center justify-between gap-4 rounded-md border border-border p-3 hover:bg-muted/50 transition-colors">
        <div>
          <span className="block text-sm font-medium">Extract comments</span>
          <span className="block text-xs text-muted-foreground">
            Pull DOCX comments and inject them inline into per-page text;
            Gotenberg also embeds them as PDF annotations. When off, comments
            are dropped from the extracted text.
          </span>
        </div>
        <Switch
          checked={extractComments}
          onCheckedChange={onExtractCommentsChange}
          className="flex-shrink-0"
        />
      </label>
    </div>
  </div>
)
